import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Paper, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNavigate } from 'react-router-dom';
import { fetchData } from '../../helpers/apiUtils';
import Explainer from '../../components/Explainer';
import { Abschnitt, Kennzeichen, NurAdmin, SchrittKarte } from '../../components/marketingForm';
import type { SchrittStatus } from '../../components/marketingForm';

/** Spiegel von `node-app/src/services/webinarUi/fahrplan.ts`. */
type FahrplanZiel =
  | { art: 'formular' }
  | { art: 'reminder' }
  | { art: 'raum'; anker?: 'video' | 'angebot' | 'automatisch' }
  | { art: 'lp'; seite: 'signup' | 'doi' | 'recording' | 'bestellstrecke' }
  | { art: 'mailings' };

interface FahrplanSchritt {
  key: string;
  block: 'bewerbung' | 'werbung' | 'verkauf' | 'nachher' | 'automatisch';
  titel: string;
  status: SchrittStatus;
  beschreibung: string;
  details: string[];
  ziel: FahrplanZiel | null;
  aktion: string | null;
}

interface FahrplanAntwort {
  webinar: {
    webinar_id: string;
    titel: string;
    interner_titel: string | null;
    termin: string | null;
    vorbei: boolean;
    produkt: string | null;
    produkt_name: string | null;
    host_name: string | null;
  };
  bewerbung: {
    status: 'bewerbbar' | 'unvollstaendig' | 'deaktiviert' | 'vorbei';
    label: string;
  } | null;
  schritte: FahrplanSchritt[];
}

/** `2026-10-25 18:00:00` → `So 25.10.2026, 18:00 Uhr` (Berliner Wandzeit, ohne Zeitzonen-Umrechnung). */
function terminText(t: string | null): string {
  const m = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})/.exec(t ?? '');
  if (!m) return 'ohne Termin';
  const tag = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'][
    new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])).getUTCDay()
  ];
  return `${tag} ${m[3]}.${m[2]}.${m[1]}, ${m[4]}:${m[5]} Uhr`;
}

const ABLAUF = [
  {
    label: 'Reihenfolge, die sich bewährt hat',
    body: (
      <>
        1. Webinar anlegen (Grunddaten). 2. Video früh hochladen — die Verarbeitung dauert. 3.
        Anmelde- und Bestätigungsseite: Inhalt vom letzten Webinar übernehmen, Texte anpassen,
        veröffentlichen. 4. Die vier Erinnerungsmails füllen — sie schalten sich automatisch ein,
        sobald beide Seiten live sind, also Texte VORHER prüfen. 5. Heat-Up-Mails unter „Webinar
        Mailings“ anlegen. 6. Bestellstrecke hochladen, prüfen, veröffentlichen, live schalten. 7.
        Kaufangebot im Raum einrichten. 8. Aufzeichnungsseite spätestens zum Start.
      </>
    ),
  },
  {
    label: 'Wann wird beworben?',
    body: 'Erst wenn alle Pflichtschritte im ersten Block grün sind. Vorher zeigt keine Anzeige, kein Newsletter und kein Portal das Webinar — Heat-Up-Mails lassen sich vorher anlegen, aber erst danach an Mailchimp übergeben. Fehlt 48 Stunden vor einer geplanten Werbung noch etwas, bekommt das Webinar-Team um 17:03 Uhr eine Warnmail mit Link hierher.',
  },
  {
    label: 'Was läuft von selbst?',
    body: 'Erinnerungsmails einschalten, Portal-Werbung am Webinartag, die Webinar-Karte auf den DOI-Seiten (aus den Heat-Up-Mails), Untertitel und — wenn das Webinar im Reiter „Wiederholung“ freigegeben ist — nach der Premiere die tägliche Wiederholung. Dafür muss niemand etwas tun.',
  },
  {
    label: 'Wichtig',
    body: 'Nichts in diesem Fahrplan verschickt Mails oder schaltet etwas live. Das passiert erst mit den Knöpfen auf den jeweiligen Seiten — und die sagen vorher, was passiert.',
    highlight: true,
  },
];

const BLOECKE: Array<{
  block: FahrplanSchritt['block'];
  titel: string;
  art?: 'pflicht' | 'pflichtVerkauf' | 'empfohlen' | 'automatisch';
  erklaerung: string;
}> = [
  {
    block: 'bewerbung',
    titel: '1. Damit das Webinar beworben werden darf',
    art: 'pflicht',
    erklaerung:
      'Sind alle Schritte grün, darf das Webinar in Anzeigen, Newslettern und auf den Portalen erscheinen.',
  },
  {
    block: 'werbung',
    titel: '2. Damit Leute davon erfahren',
    art: 'empfohlen',
    erklaerung: 'Die Werbemails vor dem Webinar — ohne sie kommen kaum Anmeldungen.',
  },
  {
    block: 'verkauf',
    titel: '3. Damit im Webinar verkauft wird',
    art: 'pflichtVerkauf',
    erklaerung: 'Ohne diese Schritte läuft das Webinar, aber es gibt keinen Kaufknopf.',
  },
  {
    block: 'nachher',
    titel: '4. Für die Zeit nach dem Webinar',
    erklaerung: 'Späte Anmelder, Nachfass-Mails und Werbung für die Aufzeichnung.',
  },
];

/**
 * Webinar-Fahrplan: alle Schritte eines Webinars in Arbeitsreihenfolge, mit Status und EINEM Knopf je Schritt.
 * Ersetzt die Checkliste „Bewerbbar?“ aus dem Bearbeiten-Formular (Plan 2026-10-22, A1). Liest nur.
 */
const WebinarFahrplan: React.FC<{
  webinarId: string;
  aktiv: boolean;
  /** Aktiv-Umschalten läuft gerade (Knopf sperren). */
  umschaltenLaeuft?: boolean;
  onBearbeiten: () => void;
  onOeffneReminder: () => void;
  onOeffneRaum: (anker?: 'video' | 'angebot' | 'automatisch') => void;
  onAktivUmschalten: () => void;
  onLoeschen: () => void;
  onCtaLinks: () => void;
}> = ({
  webinarId,
  aktiv,
  umschaltenLaeuft,
  onBearbeiten,
  onOeffneReminder,
  onOeffneRaum,
  onAktivUmschalten,
  onLoeschen,
  onCtaLinks,
}) => {
  const navigate = useNavigate();
  const [daten, setDaten] = useState<FahrplanAntwort | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(false);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    let abgebrochen = false;
    setDaten(null);
    setFehler(null);
    setLaedt(true);
    (async () => {
      const r = await fetchData<FahrplanAntwort>(
        `/api/webinar-management/webinars/${encodeURIComponent(webinarId)}/fahrplan`,
      );
      if (abgebrochen) return;
      setLaedt(false);
      if (r.success && r.data) setDaten(r.data);
      else setFehler(r.error || 'Fahrplan konnte nicht geladen werden');
    })();
    return () => {
      abgebrochen = true;
    };
  }, [webinarId, version]);

  const gehe = useCallback(
    (z: FahrplanZiel | null) => {
      if (!z) return;
      if (z.art === 'formular') onBearbeiten();
      else if (z.art === 'reminder') onOeffneReminder();
      else if (z.art === 'raum') onOeffneRaum(z.anker);
      else if (z.art === 'lp')
        navigate(`/webinar-lp?webinar=${encodeURIComponent(webinarId)}&seite=${z.seite}`);
      else if (z.art === 'mailings') navigate('/webinar-mailings');
    },
    [navigate, onBearbeiten, onOeffneRaum, onOeffneReminder, webinarId],
  );

  if (laedt && !daten) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }
  if (fehler) return <Alert severity="error">{fehler}</Alert>;
  if (!daten) return null;

  const { webinar, bewerbung, schritte } = daten;
  const pflichtOffen = schritte.filter((s) => s.block === 'bewerbung' && s.status === 'offen');
  const verkaufOffen = schritte.filter((s) => s.block === 'verkauf' && s.status === 'offen');
  const naechster = schritte.find(
    (s) =>
      (s.block === 'bewerbung' || s.block === 'werbung' || s.block === 'verkauf') &&
      s.status === 'offen',
  );

  const kopfSatz =
    bewerbung?.status === 'deaktiviert'
      ? 'Deaktiviert — wird nirgends beworben, egal was unten steht.'
      : webinar.vorbei
        ? 'Der Termin ist vorbei. Die Schritte zeigen den Stand für Aufzeichnung und Wiederholung.'
        : pflichtOffen.length === 0
          ? verkaufOffen.length === 0
            ? 'Alles bereit: das Webinar darf beworben werden und im Raum gibt es ein Kaufangebot.'
            : 'Darf beworben werden. Für den Verkauf fehlt noch etwas (Block 3).'
          : `Darf noch NICHT beworben werden — ${pflichtOffen.length} ${pflichtOffen.length === 1 ? 'Schritt fehlt' : 'Schritte fehlen'} im ersten Block.`;

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#ffffff' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: 260 }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {webinar.titel}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {terminText(webinar.termin)} ·{' '}
              {webinar.produkt_name ?? webinar.produkt ?? 'ohne Produkt'}
              {webinar.host_name ? ` · ${webinar.host_name}` : ''} · ID{' '}
              <span style={{ fontFamily: 'monospace' }}>{webinar.webinar_id}</span>
            </Typography>
            <Typography variant="body1" sx={{ mt: 1, fontWeight: 600 }}>
              {kopfSatz}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {bewerbung && (
              <Chip
                label={bewerbung.label}
                color={
                  bewerbung.status === 'bewerbbar'
                    ? 'success'
                    : bewerbung.status === 'unvollstaendig'
                      ? 'warning'
                      : 'default'
                }
              />
            )}
            <Button
              size="small"
              startIcon={<RefreshIcon />}
              onClick={() => setVersion((v) => v + 1)}
            >
              Aktualisieren
            </Button>
          </Box>
        </Box>
        {naechster && !webinar.vorbei && bewerbung?.status !== 'deaktiviert' && (
          <Alert
            severity="info"
            sx={{ mt: 1.5 }}
            action={
              naechster.aktion && naechster.ziel ? (
                <Button
                  color="inherit"
                  size="small"
                  variant="outlined"
                  onClick={() => gehe(naechster.ziel)}
                >
                  {naechster.aktion}
                </Button>
              ) : undefined
            }
          >
            <strong>Als Nächstes:</strong> {naechster.titel} — {naechster.beschreibung}
          </Alert>
        )}
      </Paper>

      <Explainer title="Wie läuft ein Webinar ab? (für Vertretungen)" sections={ABLAUF} />

      {BLOECKE.map((b) => {
        const liste = schritte.filter((s) => s.block === b.block);
        if (!liste.length) return null;
        return (
          <Box key={b.block} sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="h6" sx={{ fontSize: 16, fontWeight: 700 }}>
                {b.titel}
              </Typography>
              {b.art && <Kennzeichen art={b.art} />}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {b.erklaerung}
            </Typography>
            {liste.map((s) => (
              <SchrittKarte
                key={s.key}
                titel={s.titel}
                status={s.status}
                beschreibung={s.beschreibung}
                details={s.details}
                aktion={
                  s.aktion && s.ziel ? { label: s.aktion, onClick: () => gehe(s.ziel) } : undefined
                }
              />
            ))}
          </Box>
        );
      })}

      <Abschnitt
        titel="Läuft automatisch"
        art="automatisch"
        eingeklappt
        erklaerung="Erledigt das System selbst. Hier nur zum Nachsehen."
      >
        {schritte
          .filter((s) => s.block === 'automatisch')
          .map((s) => (
            <SchrittKarte
              key={s.key}
              titel={s.titel}
              status={s.status}
              beschreibung={s.beschreibung}
              details={s.details}
              aktion={
                s.aktion && s.ziel ? { label: s.aktion, onClick: () => gehe(s.ziel) } : undefined
              }
            />
          ))}
      </Abschnitt>

      <Abschnitt
        titel="Webinar pausieren oder löschen"
        eingeklappt
        erklaerung="Nur im Ausnahmefall. Beide Aktionen fragen vorher nach und sagen, was passiert."
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <Typography variant="subtitle2">
              {aktiv ? 'Aus aller Werbung nehmen' : 'Wieder bewerben'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {aktiv
                ? 'Nimmt das Webinar sofort aus Anzeigen, Newslettern und von den Portalen und stoppt die Erinnerungsmails. Bereits geplante Heat-Up-Mails fragt der nächste Schritt ab.'
                : 'Das Webinar ist deaktiviert. Wieder aktivieren macht es bewerbbar, sobald alle Pflichtschritte grün sind.'}
            </Typography>
            <Button
              variant="outlined"
              color={aktiv ? 'warning' : 'success'}
              size="small"
              onClick={onAktivUmschalten}
              disabled={umschaltenLaeuft}
            >
              {aktiv ? 'Deaktivieren …' : 'Wieder aktivieren'}
            </Button>
          </Box>
          <Box>
            <Typography variant="subtitle2">Webinar löschen</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Löscht den Eintrag endgültig. Geht nicht, solange Heat-Up- oder Erinnerungsmails daran
              hängen.
            </Typography>
            <Button variant="outlined" color="error" size="small" onClick={onLoeschen}>
              Löschen …
            </Button>
          </Box>
        </Box>
      </Abschnitt>

      <NurAdmin bereich titel="Altbestand (StealthSeminar / PL-X)">
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button size="small" variant="outlined" onClick={onCtaLinks}>
            Bestell- & Replay-URL
          </Button>
        </Box>
      </NurAdmin>
    </Box>
  );
};

export default WebinarFahrplan;
